<script setup lang="ts">
/**
 * A decision card's controls, as both the portal's Gates queue and the site's Control figures draw them: the
 * commands (ZhDecisionButton: Approve as a dirty-white block, Reject as a quiet outline), then any other way to act
 * (`default` slot: Request changes), and on the far edge where to look before deciding (`link` slot: Preview, Open
 * run) in the action tone. The card's own edge carries the action cyan, so the one cyan control is where to look.
 *
 * A card decided through its own form (a question answered one by one, a choice) puts its own commands in the
 * `commands` slot (Send answers, a chosen Reply), drawn with ZhDecisionButton, so they share this row and its link.
 *
 * `drawn` renders the row as a picture (spans, no buttons) for the site's figures; the card hides it from a screen
 * reader. `busy` holds both commands while a decision is on its way.
 */
import ZhDecisionButton from './ZhDecisionButton.vue'

withDefaults(
    defineProps<{
        drawn?: boolean
        busy?: boolean
        approveLabel?: string
        rejectLabel?: string
        /** The far-edge link's words when `drawn`; the portal passes a real link through the `link` slot instead. */
        linkLabel?: string
    }>(),
    { drawn: false, busy: false, approveLabel: 'Approve', rejectLabel: 'Reject' },
)

const emit = defineEmits<{ approve: []; reject: [] }>()
</script>

<template>
    <div class="zh-decision-actions">
        <slot name="commands">
            <ZhDecisionButton :drawn="drawn" :disabled="busy" @click="emit('approve')">{{ approveLabel }}</ZhDecisionButton>
            <ZhDecisionButton tone="reject" :drawn="drawn" :disabled="busy" @click="emit('reject')">
                {{ rejectLabel }}
            </ZhDecisionButton>
        </slot>
        <span v-if="$slots.default" class="zh-decision-actions__aside"><slot /></span>
        <span v-if="$slots.link || linkLabel" class="zh-decision-actions__link">
            <slot name="link">{{ linkLabel }}</slot>
        </span>
    </div>
</template>

<style scoped>
.zh-decision-actions {
    display: flex;
    flex: 1 1 100%;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

/* The control voice (BG §19.3): mono, uppercase, system tracking. */
.zh-decision-actions__aside,
.zh-decision-actions__link {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-decision-actions__aside {
    color: var(--ink-2);
}

.zh-decision-actions__link {
    margin-left: auto;
    color: var(--state-action);
}

.zh-decision-actions__aside :slotted(a),
.zh-decision-actions__link :slotted(a) {
    color: inherit;
    text-decoration: none;
    text-underline-offset: 0.2em;
}

.zh-decision-actions__aside :slotted(a:hover),
.zh-decision-actions__link :slotted(a:hover) {
    text-decoration: underline;
}

.zh-decision-actions__aside :slotted(a:focus-visible),
.zh-decision-actions__link :slotted(a:focus-visible) {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}
</style>
